import React, { useEffect, useState } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { KIND_BY_KEY } from './hubKinds';

const inp = 'w-full border rounded px-2 py-1 text-sm bg-white';
const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(', ') : typeof d === 'string' ? d : ''; };

const Field = ({ type, value, onChange, testid }) => {
  if (Array.isArray(type)) return <select className={inp} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}><option value="" />{[...(value && !type.includes(value) ? [value] : []), ...type].map((o) => <option key={o}>{o}</option>)}</select>;
  if (type === 'bool') return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />;
  return <input type={type || 'text'} step={type === 'number' ? 'any' : undefined} className={inp} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
};

const ListEditor = ({ spec, rows, onChange }) => (
  <div className="border rounded overflow-x-auto" data-testid={`hub-list-${spec.key}`}>
    <p className="px-2 py-1 bg-gray-100 text-sm font-semibold">{spec.title}</p>
    <table className="w-full text-sm min-w-[1000px]">
      <thead className="text-left"><tr>{spec.cols.map(([, l]) => <th key={l} className="px-1 py-1">{l}</th>)}<th /></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t">
          {spec.cols.map(([k, , t]) => <td key={k} className="p-1"><Field type={t} value={r[k]} onChange={(v) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)))} testid={`hub-${spec.key}-${i}-${k}`} /></td>)}
          <td className="p-1"><button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-red-600" aria-label="Remove" data-testid={`hub-${spec.key}-remove-${i}`}><Trash2 className="w-4 h-4" /></button></td>
        </tr>))}</tbody>
    </table>
    <button onClick={() => onChange([...rows, {}])} className="m-2 inline-flex items-center gap-1 text-xs border rounded px-2 py-1" data-testid={`hub-${spec.key}-add`}><Plus className="w-3 h-3" />Add</button>
  </div>
);

export const HubReview = ({ id, staff, onClose, onDone }) => {
  const [item, setItem] = useState(null);
  const [data, setData] = useState({});
  const [staffId, setStaffId] = useState('');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  useEffect(() => {
    api.get(`/upload-hub/${id}`).then((r) => { setItem(r.data); setData(r.data.reviewed || r.data.extracted || {}); }).catch(() => toast.error('Could not open the upload'));
    api.get(`/upload-hub/${id}/file`, { responseType: 'blob' }).then((r) => setUrl(URL.createObjectURL(r.data))).catch(() => {});
  }, [id]);
  useEffect(() => { // pre-pick the employee whose name is on the document
    const n = (data.employeeName || data.staffName || '').toLowerCase().trim();
    if (!staffId && n) {
      const words = n.split(/\s+/);
      const named = staff.filter((x) => (x.name || '').trim().length > 2);
      const s = named.find((x) => x.name.toLowerCase() === n) || named.find((x) => x.name.toLowerCase().split(/\s+/).every((w) => words.includes(w)));
      if (s) setStaffId(s.id);
    }
  }, [data.employeeName, data.staffName, staff, staffId]);
  if (!item) return null;
  const k = KIND_BY_KEY[item.kind];
  const done = item.status === 'Applied';
  const save = async () => {
    setBusy(true);
    try { const r = await api.post(`/upload-hub/${id}/apply`, { data, staffId: staffId || undefined }); toast.success(r.data.message, { duration: 8000 }); onDone(); onClose(); }
    catch (e) { toast.error(errText(e) || 'Could not save'); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-7xl max-h-[92vh] overflow-y-auto" data-testid="hub-review">
        <DialogHeader><DialogTitle>{k.title}{item.patientName ? ` - ${item.patientName}` : ''} <span className="text-sm font-normal text-gray-500">({item.status})</span></DialogTitle></DialogHeader>
        <div className="grid lg:grid-cols-[2fr_3fr] gap-4">
          <div className="border rounded bg-gray-50 min-h-[300px]" data-testid="hub-original">
            {url && <a href={url} target="_blank" rel="noreferrer" className="block px-2 py-1 text-xs underline" data-testid="hub-open-original">Open original in a new tab</a>}
            {url && (item.mime === 'application/pdf' ? <iframe title="original" src={url} className="w-full h-[70vh]" /> : <img src={url} alt="uploaded document" className="w-full object-contain max-h-[70vh]" />)}
          </div>
          <fieldset disabled={done || busy} className="space-y-3">
            {item.readError && <p className="text-sm text-red-700 bg-red-50 rounded p-2" data-testid="hub-read-error">{item.readError}</p>}
            {k.warn && <p className="text-sm text-amber-800 bg-amber-50 rounded p-2 flex gap-2" data-testid="hub-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{k.warn}</p>}
            {k.staff && (
              <label className="block text-sm font-semibold">Employee<select className={inp} value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="hub-staff-select">
                <option value="">Choose the employee</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
            )}
            {k.fields && <div className="grid sm:grid-cols-2 gap-2 text-sm">{k.fields.map(([f, l, t]) => <label key={f} className={t === 'bool' ? 'flex items-center gap-2' : ''}>{l}<Field type={t} value={data[f]} onChange={(v) => setData({ ...data, [f]: v })} testid={`hub-field-${f}`} /></label>)}</div>}
            {done ? <p className="text-sm bg-green-50 text-green-800 rounded p-2" data-testid="hub-result">{item.result}</p>
              : <button onClick={save} className="px-4 py-2 rounded-lg bg-gray-900 text-[#d4af37] font-semibold" data-testid="hub-apply">{busy ? 'Saving…' : 'Looks right - fill it into the app'}</button>}
          </fieldset>
        </div>
        <fieldset disabled={done || busy} className="space-y-3">
          {[k.list, k.list2].filter(Boolean).map((l) => <ListEditor key={l.key} spec={l} rows={data[l.key] || []} onChange={(rows) => setData({ ...data, [l.key]: rows })} />)}
        </fieldset>
      </DialogContent>
    </Dialog>
  );
};
